import { useState, useCallback, type ReactNode } from 'react';
import {
  Page,
  Header,
  Content,
  ContentHeader,
  InfoCard,
} from '@backstage/core-components';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

function Alert({ severity, children }: { severity: 'error' | 'success'; children: ReactNode }) {
  return (
    <Typography style={{ color: severity === 'error' ? '#c62828' : '#2e7d32', fontWeight: 500 }}>
      {children}
    </Typography>
  );
}

// The Platform Controller API this page talks to — see
// platform-controller/src/applications/. Not yet wired through Backstage's
// own config/discovery system; hardcoded for this same-box dev setup.
const PLATFORM_CONTROLLER_URL = 'http://localhost:3001';

type AppKind = 'backend' | 'ai-agent' | 'frontend' | 'worker' | 'scheduled-job' | 'automation';

const KIND_TO_TEMPLATE: Partial<Record<AppKind, string>> = {
  backend: 'backend-node-nestjs',
  'ai-agent': 'ai-agent-python',
};

const KIND_OPTIONS: { value: AppKind; label: string }[] = [
  { value: 'frontend', label: 'Frontend App' },
  { value: 'backend', label: 'Backend Service' },
  { value: 'ai-agent', label: 'AI Agent' },
  { value: 'automation', label: 'Automation' },
  { value: 'worker', label: 'Worker' },
  { value: 'scheduled-job', label: 'Scheduled Job' },
];

type Status =
  | { phase: 'idle' }
  | { phase: 'submitting' }
  | { phase: 'polling'; workflowId: string }
  | { phase: 'done'; result: Record<string, unknown> }
  | { phase: 'error'; message: string };

export function CreateApplicationPage() {
  const [advanced, setAdvanced] = useState(false);
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [kind, setKind] = useState<AppKind>('backend');
  const [runtime, setRuntime] = useState<'node' | 'python'>('node');
  const [needsS3, setNeedsS3] = useState(false);
  const [needsSqs, setNeedsSqs] = useState(false);
  const [needsDynamodb, setNeedsDynamodb] = useState(false);
  const [deploy, setDeploy] = useState(false);
  const [status, setStatus] = useState<Status>({ phase: 'idle' });

  const template = KIND_TO_TEMPLATE[kind];
  const unsupported = !template;

  const poll = useCallback(async (workflowId: string) => {
    for (;;) {
      const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications/${workflowId}`);
      const body = await res.json();
      const terminal = ['CREATED', 'DEPLOYED', 'FAILED'].includes(body.status) || Boolean(body.result);
      if (terminal) {
        setStatus({ phase: 'done', result: body });
        return;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
  }, []);

  const onSubmit = useCallback(async () => {
    if (!template) return;
    setStatus({ phase: 'submitting' });

    const rawManifest = {
      apiVersion: 'platform.mumzworld.com/v1',
      kind: 'ApplicationInfrastructure',
      // This legacy /create-app path (superseded by the 5 kind-specific
      // /create/* flows, see App.tsx) has no Department field of its own —
      // 'general' keeps it schema-valid now that InfrastructureManifestSchema
      // requires metadata.department, without building real department UI
      // for a page nothing routes to anymore.
      metadata: { application: name, owner, branch: 'development', department: 'general' },
      environment: { type: 'development' },
      application: {
        type: kind,
        runtime,
        healthcheck: '/health',
        port: runtime === 'python' ? 8000 : 3000,
      },
      compute: {},
      aws: {
        s3: needsS3 ? [{ name: `sre-idp-${name}-data`, versioning: true }] : [],
        sqs: needsSqs ? [{ name: `sre-idp-${name}-queue`, dlq: true }] : [],
        dynamodb: needsDynamodb ? [{ name: `sre-idp-${name}-state`, billingMode: 'PAY_PER_REQUEST' }] : [],
      },
      network: { public: false },
      observability: {},
      deployment: {},
    };

    try {
      const res = await fetch(`${PLATFORM_CONTROLLER_URL}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rawManifest, actorRole: 'DEVELOPER', template, deploy }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setStatus({ phase: 'error', message: JSON.stringify(body) });
        return;
      }
      const { workflowId } = await res.json();
      setStatus({ phase: 'polling', workflowId });
      await poll(workflowId);
    } catch (err) {
      setStatus({ phase: 'error', message: (err as Error).message });
    }
  }, [name, owner, kind, runtime, needsS3, needsSqs, needsDynamodb, deploy, template, poll]);

  return (
    <Page themeId="tool">
      <Header title="Create Application" subtitle="Mumzworld Internal Developer Platform" />
      <Content>
        <ContentHeader title="What do you want to build?">
          <FormControlLabel
            control={<Switch checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} />}
            label="Advanced mode"
          />
        </ContentHeader>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoCard title="Application details">
              <TextField
                fullWidth
                margin="normal"
                label="Application name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                helperText="Lowercase, hyphens only — becomes the private repo's name"
                inputProps={{ 'aria-label': 'Application name' }}
              />
              <TextField
                fullWidth
                margin="normal"
                label="Owner (team)"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                inputProps={{ 'aria-label': 'Owner (team)' }}
              />
              <TextField
                fullWidth
                select
                margin="normal"
                label="What do you want to build?"
                value={kind}
                onChange={(e) => setKind(e.target.value as AppKind)}
                SelectProps={{ SelectDisplayProps: { 'aria-label': 'What do you want to build?' } }}
              >
                {KIND_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} disabled={!KIND_TO_TEMPLATE[opt.value]}>
                    {opt.label}
                    {!KIND_TO_TEMPLATE[opt.value] ? ' (template not built yet)' : ''}
                  </MenuItem>
                ))}
              </TextField>

              {advanced && (
                <>
                  <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                    Advanced: infrastructure
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    margin="normal"
                    label="Runtime"
                    value={runtime}
                    onChange={(e) => setRuntime(e.target.value as 'node' | 'python')}
                    SelectProps={{ SelectDisplayProps: { 'aria-label': 'Runtime' } }}
                  >
                    <MenuItem value="node">node</MenuItem>
                    <MenuItem value="python">python</MenuItem>
                  </TextField>
                  <FormControlLabel
                    control={<Switch checked={needsS3} onChange={(e) => setNeedsS3(e.target.checked)} />}
                    label="Needs an S3 bucket"
                  />
                  <FormControlLabel
                    control={<Switch checked={needsSqs} onChange={(e) => setNeedsSqs(e.target.checked)} />}
                    label="Needs an SQS queue"
                  />
                  <FormControlLabel
                    control={<Switch checked={needsDynamodb} onChange={(e) => setNeedsDynamodb(e.target.checked)} />}
                    label="Needs a DynamoDB table"
                  />
                </>
              )}

              <FormControlLabel
                control={<Switch checked={deploy} onChange={(e) => setDeploy(e.target.checked)} />}
                label="Deploy to Coolify after creating"
              />

              <div style={{ marginTop: 16 }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!name || !owner || unsupported || status.phase === 'submitting' || status.phase === 'polling'}
                  onClick={onSubmit}
                >
                  {deploy ? 'Create & Deploy' : 'Create'}
                </Button>
              </div>
            </InfoCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <InfoCard title="Status">
              {status.phase === 'idle' && <Typography>Fill in the form and click Create.</Typography>}
              {status.phase === 'submitting' && <Typography>Submitting…</Typography>}
              {status.phase === 'polling' && (
                <Typography>
                  {deploy
                    ? `Creating repo and deploying (${status.workflowId})… this creates a real private GitHub repo, builds a container image, and deploys it to Coolify.`
                    : `Provisioning (${status.workflowId})… this creates a real private GitHub repo.`}
                </Typography>
              )}
              {status.phase === 'error' && <Alert severity="error">{status.message}</Alert>}
              {status.phase === 'done' && (
                <>
                  {(() => {
                    const result = (status.result as any)?.result;
                    const finalStatus = String(result?.status);
                    if (finalStatus === 'CREATED') {
                      return (
                        <Alert severity="success">
                          Created:{' '}
                          <a href={result.htmlUrl} target="_blank" rel="noreferrer">
                            {result.repoFullName}
                          </a>
                        </Alert>
                      );
                    }
                    if (finalStatus === 'DEPLOYED') {
                      return (
                        <Alert severity="success">
                          Deployed:{' '}
                          <a href={result.htmlUrl} target="_blank" rel="noreferrer">
                            {result.repoFullName}
                          </a>
                          {result.liveUrl ? (
                            <>
                              {' '}— live at{' '}
                              {/* Coolify's `domains` field already includes the
                                  scheme (e.g. "http://xxx.sslip.io") — don't
                                  prepend another one. */}
                              <a href={result.liveUrl} target="_blank" rel="noreferrer">
                                {result.liveUrl}
                              </a>
                            </>
                          ) : null}
                        </Alert>
                      );
                    }
                    return <Alert severity="error">{JSON.stringify(status.result)}</Alert>;
                  })()}
                </>
              )}
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}
